/**
 * The marks of the Pair Dadeni (M4-07).
 *
 * Five, and each one is a bargain rather than a punishment: you are alive, and
 * something about you is different for ever. The Second Branch gives us the
 * first of them almost verbatim — Bran's cauldron returns warriors who cannot
 * speak — and the rest generalise that shape: a loss that is *social* or
 * *slow*, never a stat that makes the next death likelier than the last.
 *
 * The ordering is deliberate: the two that change how other people treat you
 * come first, because those are the ones a playtest is really testing. A player
 * whose glyph has gone grey is a player everyone in the valley can see has been
 * to Annwn and come back, and the design claim of this milestone is that this
 * matters more than any number would.
 *
 * Balance note for the pass that will come: nothing here touches combat dice,
 * healing rate or wound severity, and nothing should. A mark that made you
 * worse at fighting would make the next death likelier, and a death spiral is
 * exactly the thing "nobody is deleted" exists to avoid.
 */
export const MARKS = [
    /**
     * *Mudan* — the mute. The mythic one, and socially enormous: you keep every
     * verb in the game except the one everybody actually plays with. Gestures
     * (M4-07's `gesture`) are granted in its place, which is not a consolation —
     * it is the whole of what the mark is *for*. Watching a party invent a
     * vocabulary of points and beckons around one silent member is the story this
     * mark exists to produce.
     */
    {
        id: 'mute',
        name: 'Mudan',
        examine: 'They do not speak. Not will not — cannot: the words come up and stop, and something ' +
            'behind their eyes gives up on them every time.',
        mute: true,
    },
    /**
     * *Grey-Streaked* — cosmetic, permanent, and public. It does nothing at all
     * except tell everybody who looks at you that you have been dead. That is a
     * mechanic in a game about eight people who know each other.
     */
    {
        id: 'grey',
        name: 'Grey-Streaked',
        examine: 'The colour has gone out of them — hair, beard, the whites of the eyes — as though ' +
            'something drained off and only mostly came back.',
        pale: true,
    },
    /**
     * *Hound-Dreamed* — the Cŵn Annwn have your scent now. Flavour most nights,
     * and once in a while a true warning: the hounds are uneasy because something
     * is genuinely coming. A mark that is worth having, some of the time, which
     * keeps the set from reading as a punishment ladder.
     */
    {
        id: 'hound-dreamed',
        name: 'Hound-Dreamed',
        examine: 'They sleep badly, and always the same way: white hounds with red ears, running ' +
            'somewhere just out of sight. Sometimes they wake before anybody else does.',
        hounds: true,
    },
    /**
     * *Cold-Hungered* — you came back hungry and you have been hungry since.
     * Half again as fast, which over a session is one more meal and one more
     * reason to keep the camp fed.
     */
    {
        id: 'cold-hungered',
        name: 'Cold-Hungered',
        examine: 'They eat like a man who has been cold for a long time, and are hungry again an hour ' +
            'after everyone else has stopped thinking about it.',
        hungerRate: 1.5,
    },
    /**
     * *Old-Ache* — the rain finds you now. One point of pain while it is falling,
     * charged as pain so that grit carries it and willow bark quiets it: a mark
     * you can *do something about* is a better mark than one you cannot.
     */
    {
        id: 'old-ache',
        name: 'Old-Ache',
        examine: 'They stand up like a much older person when the weather turns, and know the rain is ' +
            'coming a good hour before it does.',
        rainPain: 1,
    },
];
const MARK_INDEX = new Map(MARKS.map((mark) => [mark.id, mark]));
export const MARK_KIT = MARK_INDEX;
export function mark(id) {
    const found = MARK_INDEX.get(id);
    if (!found)
        throw new Error(`no mark '${id}'`);
    return found;
}
//# sourceMappingURL=marks.js.map